export type LocalFirstBootApp = {
  start(): Promise<void>
  hasPendingRoute(): boolean
  startNavigationSync(): Promise<void>
  startBackgroundSync(): Promise<void>
}

export type LocalFirstBootDeps = {
  settleStartupAuthCodeSession(): Promise<void>
  afterLocalBoot?: () => void
  warn?: (message: string, error: unknown) => void
}

export type DeferredSerialQueue<T> = {
  enqueue(value: T): void
}

export type LatestWinsSerialQueue<T> = {
  enqueue(value: T): Promise<void>
  clearPending(): void
}

export function createDeferredSerialQueue<T>(
  consume: (value: T) => Promise<void>,
  onError: (error: unknown) => void
): DeferredSerialQueue<T> {
  const values: T[] = []
  let scheduled = false
  let draining = false

  const schedule = (): void => {
    if (scheduled || draining) return
    scheduled = true
    queueMicrotask(() => {
      scheduled = false
      if (draining) return
      draining = true
      void (async () => {
        while (values.length) {
          const value = values.shift()
          if (value === undefined) continue
          try {
            await consume(value)
          } catch (error) {
            onError(error)
          }
        }
      })().finally(() => {
        draining = false
        if (values.length) schedule()
      })
    })
  }

  return {
    enqueue(value: T): void {
      values.push(value)
      schedule()
    }
  }
}

export function createLatestWinsSerialQueue<T>(
  consume: (value: T) => Promise<void>,
  onError: (error: unknown) => void
): LatestWinsSerialQueue<T> {
  let pending: { value: T } | undefined
  let drainPromise: Promise<void> | undefined

  const drain = async (): Promise<void> => {
    try {
      while (pending) {
        const current = pending.value
        pending = undefined
        try {
          await consume(current)
        } catch (error) {
          try {
            onError(error)
          } catch {
            // Error reporting must not poison unrelated queued work.
          }
        }
      }
    } finally {
      // Clear ownership before this drain settles, so a tail enqueue starts
      // its own drain and receives the promise for that work.
      drainPromise = undefined
    }
  }

  return {
    enqueue(value: T): Promise<void> {
      pending = { value }
      if (!drainPromise) {
        drainPromise = Promise.resolve().then(drain)
      }
      return drainPromise
    },
    clearPending(): void {
      pending = undefined
    }
  }
}

export async function startLocalFirstBoot(
  app: LocalFirstBootApp,
  deps: LocalFirstBootDeps
): Promise<{ remoteBoot: Promise<void> }> {
  await app.start()
  const remoteBoot = startRemoteAfterLocalBoot(app, deps)
  deps.afterLocalBoot?.()
  return { remoteBoot }
}

export async function startRemoteAfterLocalBoot(
  app: LocalFirstBootApp,
  deps: LocalFirstBootDeps
): Promise<void> {
  try {
    await deps.settleStartupAuthCodeSession()
  } catch (error) {
    const warn = deps.warn ?? console.warn
    warn('[auth] startup code settlement failed after local boot', error)
  } finally {
    if (app.hasPendingRoute()) {
      void app.startNavigationSync()
    } else {
      void app.startBackgroundSync()
    }
  }
}
